import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Memory Matrix — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/memory-matrix.md FTUE
 *   E1 : 3x3, 3 cellules, flash 1800 ms.
 *   E2 : 4x4, 4 cellules, flash 1500 ms.
 *   E3 : 5x5, 5 cellules, flash 1200 ms (calibration).
 *
 * Pas de scoring engine, pedagogique uniquement.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'memory_matrix.demo.step1_title',
        hintKey: 'memory_matrix.demo.step1_hint',
        gridSide: 3,
        cells: [0, 4, 7],
        flashMs: 1800,
    },
    {
        idx: 2,
        titleKey: 'memory_matrix.demo.step2_title',
        hintKey: 'memory_matrix.demo.step2_hint',
        gridSide: 4,
        cells: [1, 6, 10, 13],
        flashMs: 1500,
    },
    {
        idx: 3,
        titleKey: 'memory_matrix.demo.step3_title',
        hintKey: 'memory_matrix.demo.step3_hint',
        gridSide: 5,
        cells: [2, 6, 12, 18, 22],
        flashMs: 1200,
    },
];
const STAGE_SIZE = 280;
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('idle');
    const [picked, setPicked] = useState([]);
    const [verdict, setVerdict] = useState(null);
    const showTimerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const truthSet = new Set(step.cells);
    const k = step.cells.length;
    const clearTimer = () => {
        if (showTimerRef.current !== null) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
    };
    const startSequence = () => {
        clearTimer();
        setPicked([]);
        setVerdict(null);
        setPhase('showing');
        showTimerRef.current = setTimeout(() => {
            setPhase('awaiting');
        }, step.flashMs);
    };
    useEffect(() => () => clearTimer(), []);
    const handleTap = (idx) => {
        if (phase !== 'awaiting')
            return;
        if (picked.includes(idx))
            return;
        const newPicked = [...picked, idx];
        setPicked(newPicked);
        if (newPicked.length >= k) {
            const allHit = newPicked.every((p) => truthSet.has(p));
            setVerdict(allHit ? 'correct' : 'incorrect');
            setPhase('feedback');
        }
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('idle');
        setPicked([]);
        setVerdict(null);
    };
    const cells = [];
    for (let i = 0; i < step.gridSide * step.gridSide; i += 1)
        cells.push(i);
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('memory_matrix.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("div", { className: "grid gap-s2 border border-cyan-border-faint bg-cyan-surface p-s3", style: {
                    gridTemplateColumns: `repeat(${step.gridSide}, minmax(0, 1fr))`,
                    width: STAGE_SIZE,
                    height: STAGE_SIZE,
                }, children: cells.map((idx) => {
                    const isLit = phase === 'showing' && truthSet.has(idx);
                    const isPicked = picked.includes(idx);
                    const showFeedback = phase === 'feedback';
                    let cls = 'bg-cyan-bg text-cyan-text-secondary';
                    if (isLit)
                        cls = 'bg-cyan-network text-cyan-surface scale-95';
                    else if (showFeedback) {
                        if (verdict === 'correct' && isPicked)
                            cls = 'bg-cyan-core text-cyan-surface';
                        else if (verdict === 'incorrect') {
                            if (isPicked && !truthSet.has(idx))
                                cls = 'bg-cyan-media text-cyan-surface';
                            else if (isPicked && truthSet.has(idx))
                                cls = 'bg-cyan-core text-cyan-surface';
                            else if (!isPicked && truthSet.has(idx))
                                cls = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
                        }
                    }
                    else if (isPicked) {
                        cls = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
                    }
                    return (_jsx("button", { type: "button", disabled: phase !== 'awaiting', onClick: () => handleTap(idx), className: [
                            'flex aspect-square items-center justify-center transition-all duration-feedback ease-soft-wind',
                            cls,
                            phase === 'awaiting' ? 'cursor-pointer hover:bg-cyan-border-faint active:scale-95' : 'cursor-not-allowed',
                        ].join(' '), "aria-label": t('memory_matrix.demo.cell_aria', { idx: idx + 1 }) }, idx));
                }) }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'idle' && (_jsx("button", { type: "button", onClick: startSequence, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('memory_matrix.demo.launch') })), phase === 'showing' && (_jsx("span", { className: "font-display text-[14px] text-cyan-network", children: t('memory_matrix.demo.watch') })), phase === 'awaiting' && (_jsx("span", { className: "text-[12px] text-cyan-text-secondary", children: t('memory_matrix.demo.your_turn', { taps: picked.length, k }) })), phase === 'feedback' && (_jsxs(_Fragment, { children: [_jsx("span", { className: [
                                    'font-display text-[16px] font-medium',
                                    verdict === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                                ].join(' '), children: verdict === 'correct'
                                    ? t('memory_matrix.demo.feedback_correct')
                                    : t('memory_matrix.demo.feedback_incorrect') }), _jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') }), _jsx("button", { type: "button", onClick: startSequence, className: "border border-cyan-border-faint px-s4 py-s2 text-[12px] text-cyan-text-secondary hover:border-cyan-border", children: t('memory_matrix.demo.replay') })] })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map